import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const Navigation = () => {
  const location = useLocation();
  const { itemCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  
  const navLinks = [
    { path: '/', label: 'Start' },
    { path: '/produkt', label: 'Produkt' },
    { path: '/galerie', label: 'Galerie' },
    { path: '/configurator', label: 'Konfigurator' },
    { path: '/cart', label: 'Anfrage' },
  ];
  
  const isActive = (path) => location.pathname === path;
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-card">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="nav-logo"
          >
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary flex items-center justify-center">
              <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-black" />
            </div>
            <span className="font-heading text-base md:text-lg font-bold uppercase tracking-tight text-white group-hover:text-primary transition-colors leading-tight">
              <span className="block">Styroporkugeln24</span>
              <span className="text-xs md:text-sm font-normal text-zinc-400 normal-case tracking-normal">powered by Styroporzuschnitte24</span>
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`text-sm uppercase tracking-wider font-semibold transition-colors ${
                  isActive(link.path)
                    ? 'text-primary'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
          
          {/* Cart Button */}
          <div className="flex items-center gap-4">
            <Link to="/cart" className="relative" data-testid="nav-cart-button">
              <Button variant="ghost" size="icon" className="relative">
                <ShoppingCart className="h-5 w-5" />
                {itemCount > 0 && (
                  <span className="cart-badge" data-testid="cart-badge">
                    {itemCount}
                  </span>
                )}
              </Button>
            </Link>
            
            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-zinc-900 border-zinc-800">
                <div className="flex flex-col gap-6 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                      className={`text-lg uppercase tracking-wider font-semibold transition-colors ${
                        isActive(link.path)
                          ? 'text-primary'
                          : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
